import React, {
  Fragment,
  useCallback,
  useMemo,
  useState,
} from 'react';
import moment from 'moment';
import { format } from 'date-fns';
import { Formik } from 'formik';
import { get } from 'lodash';
import makeStyles from '@mui/styles/makeStyles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useMutation } from '@apollo/client';
import CancelButton from 'components/buttons/Cancel';
import EditButton from 'components/buttons/Edit';
import SaveButton from 'components/buttons/Save';
import CardActions from 'components/Card/Actions';
import LoadBlur from 'components/LoadBlur';
import RowSpacer from 'components/RowSpacer';
import SelectField from 'components/SelectField';
import DateField from 'components/DateField';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import { UPDATE_USER_PROFILE } from '../../../schema/apolloMutations/profileMutations';
import { GET_PROFILE_DETAIL } from '../../../schema/apolloQueries/profileQueries';
import { useAudienceTrackerContext } from '../../../lib/trackers/Audience';

const genders = {
  f: 'Female',
  m: 'Male',
  o: 'Other / Rather Not Say',
};
const useStyles = makeStyles((theme) => ({
  list: {
    padding: 0,
    color: theme.palette.text.primary,
  },
  listItem: {
    paddingTop: 0,
    paddingBottom: '20px !important',
    color: theme.palette.text.primary,
  },
}));
const ProfileAboutMePersonalForm = ({ viewer, personId }) => {
  const styles = useStyles();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [updateUserProfile] = useMutation(UPDATE_USER_PROFILE, {
    refetchQueries: [
      GET_PROFILE_DETAIL,

    ],
  });
  const { trackProfile } = useAudienceTrackerContext();

  const handleCancel = useCallback(
    () => {
      setEditing(false);
      setError(null);
    },
    [],
  );
  const handleEdit = useCallback(
    () => setEditing(true),
    [],
  );
  const initialValues = useMemo(
    () => ({
      birthdate: get(viewer, 'personInfo.birthdate') || '',
      first: get(viewer, 'personInfo.first') || '',
      gender: get(viewer, 'personInfo.gender') || '',
      last: get(viewer, 'personInfo.last') || '',
    }),
    [viewer],
  );
  const validationSchema = useMemo(
    () => Yup.object()
      .shape({
        first: Yup.string()
          .trim()
          .min(2, 'First name must be at least 2 characters')
          .max(32, 'First name cannot be more than 32 characters')
          .required('First name is required'),
        last: Yup.string()
          .trim()
          .min(2, 'Last name must be at least 2 characters')
          .max(32, 'Last name cannot be more than 32 characters')
          .required('Last name is required'),
        birthdate: Yup.date()
          .max(new Date(), 'Birthdate cannot be in the future')
          .required('Birthdate is required'),
      }),
    [],
  );

  return (
    <Formik
      enableReinitialize
      onSubmit={(data) => {
        const { first, last, birthdate, gender } = data;
        if (data.birthdate) {
          // eslint-disable-next-line no-param-reassign
          data.birthdate = format(data.birthdate, 'YYYY-MM-DD');
        }
        updateUserProfile({
          variables: {
            personId,
            person: {
              firstName: first,
              lastName: last,
              birthDate: birthdate,
              gender,
            },
          },
        }).then(res => {
          if (res.data.updateUserProfile === 200) {
            const { email } = viewer?.personInfo;
            const { postalCode, city, stateOrProvince, street1, street2 } = viewer?.addressInfo;
            trackProfile('profile', {
              first_name: first,
              last_name: last,
              gender,
              birth_date: moment(birthdate).format('YYYY-MM-DD[T]HH:mm:ss'),
              email,
              zipCode: postalCode,
              city,
              state: stateOrProvince,
              address: street1 || street2,
              // kids: kids || childrens.length,
            });
            setError(null);
            setEditing(false);
          }
        }).catch(e => {
          // eslint-disable-next-line no-console
          console.log('error in Profile personal details update, ', e);
        });

      }}
      initialValues={initialValues}
      validationSchema={validationSchema}
      isInitialValid={validationSchema.isValidSync(initialValues)}
    >
      {useCallback(
        ({
          dirty,
          errors,
          touched,
          isSubmitting,
          isValid,
          resetForm,
          setFieldTouched,
          setFieldValue,
          submitForm,
          values,
        }) => {
          const canSave = dirty && isValid && !isSubmitting;
          const maxDate = new Date();
          const minDate = new Date().getFullYear() - 100 >= Number(values?.birthdate?.split('-')[0]) ? new Date().setFullYear(new Date().getFullYear() - ((new Date().getFullYear() - Number(values?.birthdate?.split('-')[0]) + 5))) : new Date().setFullYear(new Date().getFullYear() - 100);
          return editing
            ? (
              <LoadBlur loading={isSubmitting}>
                {error && (
                  <div
                    style={{
                      color: 'red',
                      textAlign: 'center',
                    }}
                  >
                    {error}
                  </div>
                )}
                <List className={styles.list}>
                  <ListItem
                    className={styles.listItem}
                  >
                    <FormControl fullWidth>
                      <TextField
                        required
                        disabled={isSubmitting}
                        error={touched?.first && errors?.first}
                        label='First Name'
                        variant='standard'
                        fullWidth
                        name='first'
                        onChange={(e) => {
                          setFieldTouched('first', true);
                          setFieldValue('first', e.target.value);
                        }}
                        value={values.first}
                      />
                      <FormHelperText error>{touched?.first && errors?.first}</FormHelperText>
                    </FormControl>
                  </ListItem>
                  <ListItem
                    className={styles.listItem}
                  >
                    <FormControl fullWidth>
                      <TextField
                        required
                        variant='standard'
                        disabled={isSubmitting}
                        error={touched?.last && errors?.last}
                        label='Last Name'
                        fullWidth
                        onChange={(e) => {
                          setFieldTouched('last', true);
                          setFieldValue('last', e.target.value);
                        }}
                        value={values.last}
                      />
                      <FormHelperText error>{touched?.last && errors?.last}</FormHelperText>
                    </FormControl>
                  </ListItem>
                  <ListItem
                    className={styles.listItem}
                  >
                    <FormControl fullWidth>
                      <DateField
                        disabled={isSubmitting}
                        required
                        error={touched?.birthdate && errors?.birthdate}
                        errorText={errors?.birthdate}
                        floatingLabelText='Birth Date'
                        floatingLabelStyle={{ fontSize: '0.8em' }}
                        fullWidth
                        maxDate={maxDate}
                        minDate={minDate}
                        onChange={(e, val) => {
                          const date = moment(val, 'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ');
                          const formattedDate = date.format('YYYY-MM-DD');
                          setFieldTouched('birthdate', true);
                          setFieldValue('birthdate', formattedDate);
                        }}
                        value={values.birthdate}
                      />
                      <FormHelperText error>{touched?.birthdate && errors?.birthdate}</FormHelperText>
                    </FormControl>
                  </ListItem>
                  <ListItem
                    className={styles.listItem}
                  >
                    <FormControl fullWidth>
                      <FormLabel sx={{ fontSize: '0.8em' }} id='gender-label'>Gender</FormLabel>
                      <SelectField
                        labelId='gender-label'
                        id='gender'
                        disabled={isSubmitting}
                        error={errors?.gender}
                        label='Gender'
                        required
                        fullWidth
                        onChange={(event, idx, val) => {
                          setFieldTouched('gender', true);
                          setFieldValue('gender', val);
                        }}
                        placeholder='Set your gender'
                        showEmpty={values?.gender}
                        options={genders}
                        value={values.gender}
                      />
                      <FormHelperText error>{errors?.gender}</FormHelperText>
                    </FormControl>
                  </ListItem>
                </List>
                <CardActions>
                  <CancelButton
                    disabled={isSubmitting}
                    forceLabel
                    onClick={() => {
                      resetForm();
                      handleCancel();
                    }}
                    secondary
                  />
                  <RowSpacer />
                  <SaveButton
                    disabled={!canSave}
                    forceLabel
                    primary={canSave}
                    onClick={submitForm}
                  />
                </CardActions>
              </LoadBlur>
            )
            : (
              <Fragment>
                <List className={styles.list}>
                  {!values.first && !values.last && !values.birthdate && !values.gender && (
                    <ListItem
                      className={styles.listItem}
                    >
                      <ListItemText
                        secondary='Tell us a little about yourself!'
                      />
                    </ListItem>
                  )}
                  <ListItem className={styles.listItem}>
                    <ListItemText
                      primary={(
                        <span>
                          First Name
                          {!values.first && (
                            <span style={{ color: '#EF493E', marginLeft: 4, fontSize: 22, fontWeight: 600 }}>*</span>
                          )}
                        </span>
                      )}
                      secondary={
                        values.first
                          ? values.first
                          : <span style={{ fontSize: 18, color: '#fff' }}>-</span>
                      }
                    />
                  </ListItem>
                  <ListItem className={styles.listItem}>
                    <ListItemText
                      primary={(
                        <span>
                          Last Name
                          {!values.last && (
                            <span style={{ color: '#EF493E', marginLeft: 4, fontSize: 22, fontWeight: 600 }}>*</span>
                          )}
                        </span>
                      )}
                      secondary={
                        values.last
                          ? values.last
                          : <span style={{ fontSize: 18, color: '#fff' }}>-</span>
                      }
                    />
                  </ListItem>
                  <ListItem className={styles.listItem}>
                    <ListItemText
                      primary={(
                        <span>
                          Birthdate
                          {!values.birthdate && (
                            <span style={{ color: '#EF493E', marginLeft: 4, fontSize: 22, fontWeight: 600 }}>*</span>
                          )}
                        </span>
                      )}
                      secondary={
                        values.birthdate
                          ? format(values.birthdate, 'MMMM Do YYYY')
                          : <span style={{ fontSize: 18, color: '#fff' }}>-</span>
                      }
                    />
                  </ListItem>
                  <ListItem
                    className={styles.listItem}
                  >
                    <ListItemText
                      primary={(
                        <span>
                          Gender
                          {!values.gender && (
                            <span style={{ color: '#EF493E', marginLeft: 4, fontSize: 22, fontWeight: 600 }}>*</span>
                          )}
                        </span>
                      )}
                      secondary={
                        values.gender
                          ? genders[values.gender]
                          : <span style={{ fontSize: 18, color: '#fff' }}>-</span>
                      }
                    />
                  </ListItem>
                </List>
                <CardActions>
                  <RowSpacer />
                  <EditButton
                    forceLabel
                    onClick={handleEdit}
                  />
                </CardActions>
              </Fragment>
            );
        },
        [editing, error, handleCancel, handleEdit, styles.list, styles.listItem],
      )}
    </Formik>
  );
};

ProfileAboutMePersonalForm.propTypes = {
  viewer: PropTypes.object.isRequired,
  personId: PropTypes.object.isRequired,
};

export default ProfileAboutMePersonalForm;
